import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { Note } from '../domain/note'
import { concatenateNotes } from '../domain/noteOperations'
import { Icon } from './Icon'
import { TagAutocomplete } from './TagAutocomplete'
import { APP_SETTINGS } from '../config/appSettings'
import { ImageAttachments } from './ImageAttachments'
import type { NoteAsset } from '../assets/assetTypes'
import type { ViewportRuntime } from '../utils/visualViewport'
import { BrowseNotePreview } from './BrowseNotePreview'
import { NotePreviewOverlay } from './NotePreviewOverlay'
import { TagLabel } from './TagLabel'

export type SelectionAction = { kind: 'merge'; notes: Note[]; deleteOriginals: boolean; title: string }
  | { kind: 'tags'; notes: Note[]; remove: string[]; add: string[] }

export function NoteSelectionDialog({ kind, notes, tags, assets = [], remoteAssetUrls, formFactor = 'desktop', progress, onClose, onApply }: {
  kind: 'merge' | 'tags'; notes: Note[]; tags: readonly string[]
  assets?: NoteAsset[]; remoteAssetUrls?: ReadonlyMap<string, string>
  formFactor?: ViewportRuntime['formFactor']; progress?: string
  onClose: () => void; onApply: (action: SelectionAction) => Promise<void>
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const submitButton = useRef<HTMLButtonElement>(null)
  const heading = useId()
  const previewId = useId()
  const errorRef = useRef<HTMLDivElement>(null)
  const [previewNoteId, setPreviewNoteId] = useState<string | null>(null)
  const previewNote = notes.find(note => note.id === previewNoteId)
  const overlayPreview = formFactor !== 'desktop'
  const preview = (note: Note) => <BrowseNotePreview note={note} assets={assets} remoteAssetUrls={remoteAssetUrls} />
  const [ordered, setOrdered] = useState(notes)
  const [title, setTitle] = useState('')
  const [deleteOriginals, setDeleteOriginals] = useState(false)
  const [remove, setRemove] = useState<string[]>([])
  const [add, setAdd] = useState<string[]>([])
  const [query, setQuery] = useState('')
  const [busy, setBusy] = useState(false)
  const lock = useRef(false)
  const [error, setError] = useState('')
  useEffect(() => { if (error) errorRef.current?.focus({ preventScroll: true }) }, [error])
  const usedTags = useMemo(() => {
    const counts = new Map<string, number>()
    for (const note of notes) for (const tag of new Set(note.userTags)) counts.set(tag, (counts.get(tag) ?? 0) + 1)
    return [...counts].sort(([a], [b]) => a.localeCompare(b))
  }, [notes])
  useEffect(() => {
    const element = dialog.current!
    const previousFocus = document.activeElement
    element.showModal()
    return () => {
      element.close()
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true })
    }
  }, [])
  const move = (index: number, direction: number) => setOrdered(current => {
    const next = [...current]
    ;[next[index], next[index + direction]] = [next[index + direction]!, next[index]!]
    return next
  })
  const submit = async () => {
    if (lock.current) return
    lock.current = true; setBusy(true); setError('')
    try {
      await onApply(kind === 'merge' ? { kind, notes: ordered, deleteOriginals, title } : { kind, notes, remove, add })
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Could not save this action. Try again.')
    } finally { lock.current = false; setBusy(false) }
  }
  return <><dialog ref={dialog} className="modal note-selection-dialog" aria-labelledby={heading}
    onCancel={event => { event.preventDefault(); if (!lock.current) onClose() }}>
    <form className="modal-box note-selection-dialog__panel" onSubmit={event => { event.preventDefault(); void submit() }}>
      <h2 id={heading} tabIndex={-1} autoFocus={kind === 'merge'}>{kind === 'merge' ? `Merge ${notes.length} notes` : `Edit tags on ${notes.length} notes`}</h2>
      <div className="note-selection-dialog__scroll">
        <fieldset disabled={busy}>
          {kind === 'merge' ? <>
            <label className="merge-title-field">
              <span>Title (optional)</span>
              <input className="input" type="text" value={title} onChange={event => setTitle(event.target.value)} placeholder="Merged note title" />
            </label>
            <p>desired merging order:</p>
            <ol className="merge-order" aria-label="Concatenation order">
              {ordered.map((note, index) => <li key={note.id}>
                <span className="merge-order__number">{index + 1}</span>
                <div className="merge-order__source">
                <div className="merge-order__details">
                <button type="button" className="btn btn-ghost merge-order__toggle"
                  aria-expanded={!overlayPreview ? previewNoteId === note.id : undefined}
                  aria-controls={!overlayPreview && previewNoteId === note.id ? previewId : undefined}
                  aria-haspopup={overlayPreview ? 'dialog' : undefined}
                  onClick={() => setPreviewNoteId(current => current === note.id ? null : note.id)}>
                  <Icon name="chevron" /><span>{note.manualTitle || note.derivedTitle || 'Untitled note'}</span>
                </button>
                {note.userTags.length > 0 && <div className="merge-order__tags" aria-label={`Note tags: ${note.userTags.join(', ')}`}>
                  {note.userTags.map(tag => <span key={tag} className="badge"><TagLabel tag={tag} /></span>)}
                </div>}
                </div>
                {note.assetIds.length > 0 && <div className="merge-order__images">
                  <small>{note.assetIds.length} {note.assetIds.length === 1 ? 'image' : 'images'}{note.assetIds.length > APP_SETTINGS.browse.mergePreviewMaxImages ? ` · showing ${APP_SETTINGS.browse.mergePreviewMaxImages}` : ''}</small>
                  <ImageAttachments assets={note.assetIds.slice(0, APP_SETTINGS.browse.mergePreviewMaxImages).flatMap(id => { const asset = assets.find(item => item.id === id); return asset ? [asset] : [] })}
                    remoteUrls={remoteAssetUrls} compact compactGallery expectedCount={Math.min(note.assetIds.length, APP_SETTINGS.browse.mergePreviewMaxImages)} />
                </div>}
                </div>
                <div className="merge-order__actions">
                  <button type="button" className="btn btn-ghost" disabled={busy || index === 0} onClick={() => move(index, -1)} aria-label={`Move note ${index + 1} up`}><Icon name="arrow-up" /></button>
                  <button type="button" className="btn btn-ghost" disabled={busy || index === ordered.length - 1} onClick={() => move(index, 1)} aria-label={`Move note ${index + 1} down`}><Icon name="arrow-down" /></button>
                </div>
                {!overlayPreview && previewNoteId === note.id && <div id={previewId} className="merge-order__preview">{preview(note)}</div>}
              </li>)}
            </ol>
            <details className="merge-preview"><summary>Preview combined text</summary><pre>{concatenateNotes(ordered, title)}</pre></details>
            <label className="merge-delete-choice"><input type="checkbox" checked={deleteOriginals} onChange={event => setDeleteOriginals(event.target.checked)} />
              <span>Delete original notes after merging <small>Moves originals to Trash. Undo restores them.</small></span></label>
          </> : <>
            <h3>Add to all selected notes</h3>
            <TagAutocomplete inputRef={input} value={query} onValueChange={setQuery} tags={tags} selectedTags={add}
              showSuggestionsWhenEmpty={false}
              onAddTag={tag => { setAdd(current => [...new Set([...current, tag])]); setRemove(current => current.filter(value => value !== tag)) }}
              onRemoveTag={tag => setAdd(current => current.filter(value => value !== tag))}
              onClose={() => submitButton.current?.focus()} />
            <div className="bulk-tags__heading"><h3>Applied tags (tap to remove)</h3>
              <button type="button" className="btn btn-ghost" disabled={busy || usedTags.length === remove.length} onClick={() => setRemove(usedTags.map(([tag]) => tag))}>Clear all</button></div>
            <div className="bulk-tags__existing">
              {usedTags.length === 0 && <p>No tags on these notes.</p>}
              {usedTags.map(([tag, count]) => <button key={tag} type="button" className="btn btn-ghost bulk-tags__tag"
                data-removing={remove.includes(tag)} aria-pressed={remove.includes(tag)}
                aria-label={`${remove.includes(tag) ? 'Keep' : 'Remove'} tag ${tag}, used on ${count} of ${notes.length} notes`}
                onClick={() => { setRemove(current => current.includes(tag) ? current.filter(value => value !== tag) : [...current, tag]); setAdd(current => current.filter(value => value !== tag)) }}>
                <span><TagLabel tag={tag} showPrefix={false} /></span><small>({count}/{notes.length})</small><Icon name={remove.includes(tag) ? 'undo' : 'close'} />
              </button>)}
            </div>
            {remove.length > 0 && <p role="status">{add.length > 0
              ? `remove ${remove.length} tag${remove.length === 1 ? '' : 's'}, add ${add.length}`
              : `Removing ${remove.length} tag${remove.length === 1 ? '' : 's'}. Click a marked tag to keep it.`}</p>}
          </>}
        </fieldset>
      </div>
      {busy && <p className="note-selection-dialog__progress" role="status">{progress || (kind === 'merge' ? 'Preparing the merged note…' : 'Updating tags…')}</p>}
      {error && <div ref={errorRef} tabIndex={-1} className="note-selection-dialog__error" role="alert">
        <strong>{kind === 'merge' ? 'Merge not completed. No new note was created.' : 'Tags were not updated.'}</strong>
        <p>{error}</p>
      </div>}
      <div className="modal-action">
        <button type="button" className="btn btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
        <button ref={submitButton} type="submit" className="btn btn-primary" disabled={busy || (kind === 'merge' ? notes.length < 2 : (!remove.length && !add.length) || Boolean(query.trim()))}>
          {busy ? kind === 'merge' ? 'Merging…' : 'Saving…' : kind === 'merge' ? error ? 'Retry merge' : 'Merge into new note' : 'Apply tags'}
        </button>
      </div>
    </form>
  </dialog>
  {overlayPreview && previewNote && <NotePreviewOverlay onClose={() => setPreviewNoteId(null)}>{preview(previewNote)}</NotePreviewOverlay>}
  </>
}
